import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { connectionInvitationsApi } from '../utils/api';

// Public landing page for /invite/:token. Two accept paths:
//   - Signed-in users see a single "Accept with [their active workspace]"
//     button that posts to the authenticated endpoint
//   - Anonymous users see an inline signup form that creates a new
//     workspace + accepts the invitation in one transaction
// If the user is already signed in but needs to accept with a different
// workspace, we point them at the workspace switcher (nav sidebar).

const statusCopy = {
  expired: {
    title: 'Invitation expired',
    body: 'This invitation link is no longer valid. Ask the sender for a new one.',
  },
  accepted: {
    title: 'Invitation already accepted',
    body: 'This invitation has already been used. Check your Connections list.',
  },
  declined: {
    title: 'Invitation declined',
    body: 'This invitation was declined.',
  },
};

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, workspace, refreshMe } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    workspaceName: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await connectionInvitationsApi.lookup(token);
        setInvitation(res.data.invitation);
        setForm((f) => ({
          ...f,
          email: res.data.invitation.invitedEmail || '',
          workspaceName: res.data.invitation.invitedWorkspaceName || '',
        }));
      } catch (err) {
        setError(err.response?.data?.error || 'Invitation not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAuthenticatedAccept = async () => {
    setAccepting(true);
    try {
      const authToken = localStorage.getItem('accessToken');
      await connectionInvitationsApi.accept(token, null, { authToken });
      await refreshMe();
      navigate('/connections');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept');
      setAccepting(false);
    }
  };

  const handleAnonymousAccept = async (e) => {
    e.preventDefault();
    setAccepting(true);
    setError('');
    try {
      const res = await connectionInvitationsApi.accept(token, form);
      // Persist the issued tokens so the user is logged in.
      localStorage.setItem('accessToken', res.data.accessToken);
      localStorage.setItem('refreshToken', res.data.refreshToken);
      // Hard navigate to /connections so AuthContext rehydrates on mount.
      window.location.href = '/connections';
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full card p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation unavailable</h1>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <Link to="/login" className="text-primary-600 hover:text-primary-700 text-sm">Go to sign in</Link>
        </div>
      </div>
    );
  }

  // Terminal states (expired / accepted / declined)
  const terminal = statusCopy[invitation?.status];
  if (terminal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full card p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{terminal.title}</h1>
          <p className="text-sm text-gray-600 mb-4">{terminal.body}</p>
          <Link to="/login" className="text-primary-600 hover:text-primary-700 text-sm">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl mb-3">
            <span className="text-white font-bold text-xl">TB</span>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {invitation.initiatorWorkspace.name} invited you
          </h1>
          <p className="text-gray-600 mt-1">
            to work together on Verdict — you'd be the <strong>{invitation.counterpartyRole === 'buyer' ? 'client' : 'agency'}</strong>
          </p>
        </div>

        {invitation.contextNote && (
          <div className="card p-4 mb-4">
            <p className="text-xs text-gray-600 uppercase mb-1">Note from sender</p>
            <p className="text-sm text-gray-700">{invitation.contextNote}</p>
          </div>
        )}

        <div className="card p-6">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>
          )}

          {isAuthenticated ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Signed in as <strong>{user?.name}</strong> ({workspace?.name}).
              </p>
              <button
                onClick={handleAuthenticatedAccept}
                disabled={accepting}
                className="btn-primary w-full py-3"
              >
                {accepting ? 'Accepting...' : `Accept as ${workspace?.name}`}
              </button>
              <p className="mt-3 text-xs text-gray-600 text-center">
                Want to accept with a different workspace? Use the workspace switcher in the sidebar.
              </p>
            </>
          ) : (
            <form onSubmit={handleAnonymousAccept} className="space-y-4">
              <p className="text-sm text-gray-600">
                Create a free workspace to accept. No credit card required.
              </p>
              <div>
                <label htmlFor="workspaceName" className="label">Workspace name</label>
                <input
                  id="workspaceName"
                  type="text"
                  required
                  className="input"
                  value={form.workspaceName}
                  onChange={(e) => setForm({ ...form, workspaceName: e.target.value })}
                  placeholder="Acme Law"
                />
              </div>
              <div>
                <label htmlFor="name" className="label">Your name</label>
                <input
                  id="name"
                  type="text"
                  required
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="email" className="label">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  className="input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label htmlFor="password" className="label">Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  className="input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 8 characters"
                />
              </div>
              <button type="submit" disabled={accepting} className="btn-primary w-full py-3">
                {accepting ? 'Creating...' : 'Create workspace & accept'}
              </button>
              <p className="text-xs text-gray-600 text-center">
                Already have an account?{' '}
                <Link to={`/login?returnTo=/invite/${token}`} className="text-primary-600 hover:text-primary-700">
                  Sign in to accept
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
